import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCommunity } from '@/contexts/CommunityContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Table, TableBody, TableCell, TableHead, 
  TableHeader, TableRow 
} from '@/components/ui/table';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { 
  Select, SelectContent, SelectItem, 
  SelectTrigger, SelectValue 
} from "@/components/ui/select";
import { 
  Send, Users, Eye, AlertTriangle, CheckCircle, 
  Clock, Mail, Target, Loader2, Filter 
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { toast } from 'sonner';
import CampaignEmailPreviewModal from '@/components/admin/CampaignEmailPreviewModal';
import { Label } from '@/components/ui/label';
import { type TemplateType } from '@/utils/emailPreviewUtils';

// Component for displaying campaign status badges
const CampaignStatusBadge = ({ status, lastSentAt }: { status: 'sent' | 'never'; lastSentAt?: string }) => {
  if (status === 'never') {
    return (
      <Badge variant="secondary" className="bg-orange-100 text-orange-800 border-orange-200">
        Never
      </Badge>
    );
  }
  
  return (
    <Badge variant="secondary" className="bg-green-100 text-green-800 border-green-200 flex items-center gap-1">
      <CheckCircle className="h-3 w-3" />
      {lastSentAt ? `Sent ${format(new Date(lastSentAt), 'MMM d')}` : 'Sent'}
    </Badge>
  );
};

interface DateFilter {
  field: string;
  operator: string;
  value: number;
  unit: string;
}

interface RuleConfig {
  tables: string[];
  conditions: any[];
  date_filter?: DateFilter;
  order_by?: string;
}

interface CampaignRule {
  id: string;
  name: string;
  description: string;
  rule_config: RuleConfig;
  template_type: string;
}

interface TargetedUser {
  user_id: string;
  business_id: string;
  user_email: string;
  user_name: string;
  business_name: string;
  created_at: string;
  last_sent_at?: string;
  campaign_status: 'sent' | 'never';
}

interface Campaign {
  id: string;
  name: string;
  status: string;
  total_recipients: number;
  emails_sent: number;
  emails_failed: number;
  executed_at: string;
  completed_at: string;
  error_details: Record<string, unknown> | null;
  campaign_rules: {
    name: string;
  };
}

const CampaignManagement = () => {
  const { currentCommunity } = useCommunity();
  const queryClient = useQueryClient();
  
  const [selectedRule, setSelectedRule] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [showExecuteDialog, setShowExecuteDialog] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState<string[]>([]);
  const [dateFilterOverride, setDateFilterOverride] = useState<number | null>(null);
  const [appliedDateFilter, setAppliedDateFilter] = useState<number | null>(null);
  const [recipientFilter, setRecipientFilter] = useState<string>('all');

  // Reset state when campaign rule changes
  useEffect(() => {
    if (selectedRule) {
      setShowPreview(false);
      setShowEmailPreview(false);
      setShowExecuteDialog(false);
      setSelectedRecipients([]);
      setDateFilterOverride(null); // Always reset to null, let user decide the value
      setAppliedDateFilter(null);
      setRecipientFilter('all');
    }
  }, [selectedRule]);

  // Fetch campaign rules
  const { data: campaignRules = [] } = useQuery({
    queryKey: ['admin', 'campaign-rules', currentCommunity.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('campaign_rules')
        .select('*')
        .eq('community_id', currentCommunity.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return (data || []) as unknown as CampaignRule[];
    },
    enabled: !!currentCommunity.id
  });

  // Fetch targeted users for preview
  const { data: targetedUsers = [], isLoading: isLoadingPreview, refetch: refetchPreview } = useQuery({
    queryKey: ['admin', 'campaign-preview', selectedRule, currentCommunity.id, appliedDateFilter],
    queryFn: async () => {
      if (!selectedRule) return [];
      
      const { data, error } = await supabase
        .rpc('evaluate_campaign_rule', {
          rule_id: selectedRule,
          community_id_param: currentCommunity.id,
          limit_param: 100,
          date_override_value: appliedDateFilter
        });
      
      if (error) throw error;
      return (data || []) as unknown as TargetedUser[];
    },
    enabled: !!selectedRule && !!currentCommunity.id && showPreview
  });

  // Fetch recent campaigns
  const { data: recentCampaigns = [] } = useQuery({
    queryKey: ['admin', 'campaigns', currentCommunity.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('campaigns')
        .select(`
          *,
          campaign_rules!inner(name)
        `)
        .eq('community_id', currentCommunity.id)
        .not('executed_at', 'is', null)
        .order('executed_at', { ascending: false })
        .limit(10);
      
      if (error) throw error;
      return (data || []) as unknown as Campaign[];
    },
    enabled: !!currentCommunity.id
  });

  // Execute campaign mutation
  const executeCampaignMutation = useMutation({
    mutationFn: async ({ ruleId, recipientIds }: { ruleId: string; recipientIds: string[] }) => {
      // Get selected rule details for campaign creation
      const selectedRuleData = campaignRules.find(rule => rule.id === ruleId);
      if (!selectedRuleData) throw new Error('Campaign rule not found');

      // Prepare payload for Edge Function - it will handle campaign/execution creation
      // The Edge Function will derive rule_name and template_type from rule_id
      const campaignPayload = {
        rule_id: ruleId,
        community_id: currentCommunity.id,
        date_override_value: appliedDateFilter, // Include the applied date filter (from preview) for audit trail
        recipients: targetedUsers
          .filter(user => recipientIds.includes(user.user_id))
          .map(user => ({
            user_id: user.user_id,
            business_id: user.business_id,
            user_email: user.user_email,
            user_name: user.user_name,
            business_name: user.business_name
          }))
      };

      // Call send-campaign-emails Edge Function
      const response = await supabase.functions.invoke('send-campaign-emails', {
        body: campaignPayload
      });

      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(`Campaign executed successfully! ${data.emails_sent} emails sent.`);
      queryClient.invalidateQueries({ queryKey: ['admin', 'campaigns'] });
      setShowExecuteDialog(false);
      setShowEmailPreview(false);
      setSelectedRecipients([]);
      setShowPreview(false);
      setSelectedRule('');
    },
    onError: (error: Error) => {
      toast.error(`Failed to execute campaign: ${error.message}`);
    }
  });

  const handlePreview = () => {
    if (!selectedRule) {
      toast.error('Please select a campaign rule first');
      return;
    }
    // Apply the current date filter override value when preview is clicked
    setAppliedDateFilter(dateFilterOverride);
    setShowPreview(true);
    refetchPreview();
  };

  const handleSelectAll = () => {
    const allIds = filteredTargetedUsers.map(user => user.user_id);
    setSelectedRecipients(allIds);
  };

  const handleDeselectAll = () => {
    setSelectedRecipients([]);
  };

  const handleRecipientToggle = (userId: string) => {
    setSelectedRecipients(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Filter targeted users based on campaign status
  const filteredTargetedUsers = targetedUsers.filter(user => {
    switch (recipientFilter) {
      case 'never':
        return user.campaign_status === 'never';
      case 'sent':
        return user.campaign_status === 'sent';
      case 'recent':
        if (user.campaign_status === 'never' || !user.last_sent_at) return false;
        const daysDiff = (Date.now() - new Date(user.last_sent_at).getTime()) / (1000 * 60 * 60 * 24);
        return daysDiff <= 30;
      default:
        return true;
    }
  });

  // Helper functions for smart selection
  const handleSelectNeverSent = () => {
    const neverSentIds = filteredTargetedUsers
      .filter(user => user.campaign_status === 'never')
      .map(user => user.user_id);
    setSelectedRecipients(neverSentIds);
  };

  const handleSelectAlreadySent = () => {
    const alreadySentIds = filteredTargetedUsers
      .filter(user => user.campaign_status === 'sent')
      .map(user => user.user_id);
    setSelectedRecipients(alreadySentIds);
  };

  const handleExecuteCampaign = () => {
    if (selectedRecipients.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }
    
    // Show warning if sending to many already sent users
    const alreadySentCount = selectedRecipients.filter(id => 
      targetedUsers.find(user => user.user_id === id)?.campaign_status === 'sent'
    ).length;
    
    if (alreadySentCount > selectedRecipients.length * 0.5) {
      toast.warning(`Warning: ${alreadySentCount} of ${selectedRecipients.length} recipients have already received this campaign type.`);
    }
    
    setShowEmailPreview(true);
  };

  const handleEmailPreviewConfirm = () => {
    setShowEmailPreview(false);
    setShowExecuteDialog(true);
  };

  const confirmExecuteCampaign = () => {
    executeCampaignMutation.mutate({
      ruleId: selectedRule,
      recipientIds: selectedRecipients
    });
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { 
        variant: 'secondary' as const, 
        icon: Clock, 
        label: 'Pending',
        className: 'bg-yellow-100 text-yellow-800 border-yellow-200'
      },
      running: { 
        variant: 'default' as const, 
        icon: Loader2, 
        label: 'Running',
        className: 'bg-blue-100 text-blue-800 border-blue-200'
      },
      completed: { 
        variant: 'default' as const, 
        icon: CheckCircle, 
        label: 'Completed',
        className: 'bg-green-100 text-green-800 border-green-200'
      },
      failed: { 
        variant: 'destructive' as const, 
        icon: AlertTriangle, 
        label: 'Failed',
        className: 'bg-red-100 text-red-800 border-red-200'
      },
      cancelled: { 
        variant: 'secondary' as const, 
        icon: AlertTriangle, 
        label: 'Cancelled',
        className: 'bg-gray-100 text-gray-800 border-gray-200'
      }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className={`flex items-center gap-1 ${config.className}`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6 lg:space-y-8 animate-fade-in p-1 sm:p-0">
      <div className="relative px-1">
        <h1 className="admin-section-title mb-2 text-2xl sm:text-3xl">Campaign Management</h1>
        <p className="text-muted-foreground/80 text-base sm:text-lg">
          Send targeted emails to specific audiences in your community (max 100 recipients per campaign)
        </p>
      </div>
      
      {/* Create New Campaign Card */}
      <Card className="admin-card admin-card-stats">
        <CardHeader className="pb-4 sm:pb-6">
          <CardTitle className="text-lg sm:text-xl font-bold text-slate-800 flex items-center gap-2">
            <Target className="h-5 w-5" />
            Create New Campaign
          </CardTitle>
          <CardDescription className="text-muted-foreground/80 mt-2 text-sm sm:text-base">
            Select a targeting rule and preview recipients before sending
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-4">
            {/* Campaign Rule Selection */}
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Campaign Rule</label>
              <Select value={selectedRule} onValueChange={setSelectedRule}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a campaign rule" />
                </SelectTrigger>
                <SelectContent>
                  {campaignRules.map((rule) => (
                    <SelectItem key={rule.id} value={rule.id}>
                      {rule.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Rule Description */}
            {selectedRule && (
              <p className="text-sm text-muted-foreground -mt-2">
                {campaignRules.find(r => r.id === selectedRule)?.description}
              </p>
            )}
            
            {/* Date Filter and Preview Button Row */}
            {selectedRule && (
              <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
                {/* Date Filter Override UI - Only show if rule has date_filter */}
                {(() => {
                  const selectedRuleData = campaignRules.find(r => r.id === selectedRule);
                  const dateFilter = selectedRuleData?.rule_config?.date_filter;
                  if (!dateFilter) return null;
                  
                  return (
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg sm:w-auto">
                      <Label className="font-medium whitespace-nowrap">Time Period:</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="text"
                          value={dateFilterOverride === null ? '' : dateFilterOverride.toString()}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Allow empty string for user to clear and type new value
                            if (value === '') {
                              setDateFilterOverride(null);
                            } else {
                              const numValue = parseInt(value, 10);
                              if (!isNaN(numValue)) {
                                setDateFilterOverride(numValue);
                              }
                            }
                          }}
                          placeholder={dateFilter.value.toString()}
                          className="w-20"
                        />
                        <span className="text-sm text-muted-foreground">
                          {dateFilter.unit}
                        </span>
                      </div>
                      <span className="hidden lg:inline text-sm text-muted-foreground italic">
                        ({dateFilter.field.includes('businesses') ? 'Businesses' : 'Users'} created {dateFilterOverride ?? dateFilter.value} {dateFilter.unit} ago or more)
                      </span>
                    </div>
                  );
                })()}
                
                {/* Preview Button - Always show */}
                <Button 
                  onClick={handlePreview}
                  disabled={!selectedRule}
                  variant="outline"
                  className="flex items-center gap-2 w-full sm:w-auto"
                >
                  <Eye className="h-4 w-4" />
                  Preview Recipients
                </Button>
              </div>
            )}
          </div>

          {/* Preview Results */}
          {showPreview && (
            <div className="border rounded-lg p-3 sm:p-4 bg-gray-50">
              {/* Header - Mobile/Desktop Responsive */}
              <div className="space-y-3 sm:space-y-0 sm:flex sm:items-center sm:justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2 text-sm sm:text-base">
                  <Users className="h-4 w-4 flex-shrink-0" />
                  <span className="break-words">Targeted Recipients ({filteredTargetedUsers.length})</span>
                  {recipientFilter !== 'all' && (
                    <span className="text-xs sm:text-sm text-muted-foreground hidden sm:inline">
                      (filtered from {targetedUsers.length})
                    </span>
                  )}
                </h3>
                {filteredTargetedUsers.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={handleSelectAll} className="flex-1 sm:flex-none text-xs">
                      Select All
                    </Button>
                    <Button size="sm" variant="outline" onClick={handleDeselectAll} className="flex-1 sm:flex-none text-xs">
                      Deselect All
                    </Button>
                  </div>
                )}
              </div>

              {/* Filter Controls */}
              <div className="space-y-3 sm:space-y-0 sm:flex sm:items-center sm:justify-between mb-4">
                {/* Filter Dropdown */}
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 flex-shrink-0" />
                  <Label className="text-sm font-medium whitespace-nowrap">Filter:</Label>
                  <Select value={recipientFilter} onValueChange={setRecipientFilter}>
                    <SelectTrigger className="w-full sm:w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Recipients</SelectItem>
                      <SelectItem value="never">Never Sent Only</SelectItem>
                      <SelectItem value="sent">Already Sent Only</SelectItem>
                      <SelectItem value="recent">Recent (30 days)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Smart Selection Buttons */}
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={handleSelectNeverSent}
                    disabled={filteredTargetedUsers.filter(u => u.campaign_status === 'never').length === 0}
                    className="w-full sm:w-auto text-xs sm:text-sm"
                  >
                    Select Never Sent
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={handleSelectAlreadySent}
                    disabled={filteredTargetedUsers.filter(u => u.campaign_status === 'sent').length === 0}
                    className="w-full sm:w-auto text-xs sm:text-sm"
                  >
                    Select Already Sent
                  </Button>
                </div>
              </div>

              {/* Campaign Statistics */}
              {targetedUsers.length > 0 && (
                <div className="flex flex-wrap gap-4 mb-4 text-sm text-muted-foreground">
                  <span>
                    Never sent: {targetedUsers.filter(u => u.campaign_status === 'never').length}
                  </span>
                  <span>
                    Already sent: {targetedUsers.filter(u => u.campaign_status === 'sent').length}
                  </span>
                </div>
              )}

              {isLoadingPreview ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : filteredTargetedUsers.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No recipients found matching the selected rule
                </div>
              ) : (
                <>
                  {/* Desktop Table View */}
                  <div className="hidden sm:block">
                    <div className="max-h-96 overflow-y-auto mb-4 rounded-lg border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12">
                              <input 
                                type="checkbox" 
                                checked={selectedRecipients.length === filteredTargetedUsers.length}
                                onChange={selectedRecipients.length === filteredTargetedUsers.length ? handleDeselectAll : handleSelectAll}
                              />
                            </TableHead>
                            <TableHead>User</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Business</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Created</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredTargetedUsers.map((user) => (
                            <TableRow key={user.user_id}>
                              <TableCell>
                                <input 
                                  type="checkbox" 
                                  checked={selectedRecipients.includes(user.user_id)}
                                  onChange={() => handleRecipientToggle(user.user_id)}
                                />
                              </TableCell>
                              <TableCell className="font-medium">{user.user_name}</TableCell>
                              <TableCell className="text-sm text-muted-foreground">{user.user_email}</TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {user.business_name || '-'}
                              </TableCell>
                              <TableCell>
                                <CampaignStatusBadge 
                                  status={user.campaign_status}
                                  lastSentAt={user.last_sent_at}
                                />
                              </TableCell>
                              <TableCell className="text-sm">
                                {format(new Date(user.created_at), 'MMM d, yyyy')}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>

                  {/* Mobile Card View */}
                  <div className="sm:hidden">
                    <div className="max-h-96 overflow-y-auto mb-4 space-y-3">
                      {filteredTargetedUsers.map((user) => (
                        <div key={user.user_id} className="bg-white rounded-lg border p-3">
                          <div className="flex items-start gap-3">
                            <input 
                              type="checkbox" 
                              checked={selectedRecipients.includes(user.user_id)}
                              onChange={() => handleRecipientToggle(user.user_id)}
                              className="mt-1"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <div className="font-medium text-sm">{user.user_name}</div>
                                <CampaignStatusBadge 
                                  status={user.campaign_status}
                                  lastSentAt={user.last_sent_at}
                                />
                              </div>
                              <div className="text-xs text-muted-foreground truncate">{user.user_email}</div>
                              {user.business_name && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  Business: {user.business_name}
                                </div>
                              )}
                              <div className="text-xs text-muted-foreground mt-1">
                                Created: {format(new Date(user.created_at), 'MMM d, yyyy')}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Footer - Mobile/Desktop Responsive */}
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 pt-4 border-t">
                    <p className="text-sm text-muted-foreground text-center sm:text-left">
                      {selectedRecipients.length} of {filteredTargetedUsers.length} recipients selected
                    </p>
                    <Button 
                      onClick={handleExecuteCampaign}
                      disabled={selectedRecipients.length === 0}
                      className="flex items-center justify-center gap-2 w-full sm:w-auto"
                    >
                      <Send className="h-4 w-4" />
                      Send Campaign
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Campaigns */}
      <Card className="admin-card admin-card-stats">
        <CardHeader>
          <CardTitle className="text-lg sm:text-xl font-bold text-slate-800 flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Recent Campaigns
          </CardTitle>
          <CardDescription>
            Track the status and results of your recent email campaigns
          </CardDescription>
        </CardHeader>
        <CardContent>
          {recentCampaigns.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No campaigns executed yet
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden sm:block">
                <div className="rounded-xl border border-gray-200/60 overflow-hidden bg-white shadow-sm overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-b border-gray-100 bg-gray-50/50">
                        <TableHead>Campaign Rule</TableHead>
                        <TableHead>Recipients</TableHead>
                        <TableHead>Sent</TableHead>
                        <TableHead>Failed</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Executed</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recentCampaigns.map((campaign) => (
                        <TableRow key={campaign.id}>
                          <TableCell className="font-medium">
                            {campaign.campaign_rules.name}
                          </TableCell>
                          <TableCell>{campaign.total_recipients}</TableCell>
                          <TableCell className="text-green-600 font-medium">
                            {campaign.emails_sent}
                          </TableCell>
                          <TableCell className="text-red-600 font-medium">
                            {campaign.emails_failed}
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(campaign.status)}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {format(new Date(campaign.executed_at), 'MMM d, yyyy HH:mm')}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Mobile Card View */}
              <div className="sm:hidden space-y-4">
                {recentCampaigns.map((campaign) => (
                  <div key={campaign.id} className="bg-white rounded-lg border p-4 relative">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm truncate">{campaign.campaign_rules.name}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          {getStatusBadge(campaign.status)}
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 text-sm mb-2">
                      <div>
                        <span className="text-muted-foreground">Recipients:</span>
                        <span className="ml-1 font-medium">{campaign.total_recipients}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Sent:</span>
                        <span className="ml-1 font-medium text-green-600">{campaign.emails_sent}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Failed:</span>
                        <span className="ml-1 font-medium text-red-600">{campaign.emails_failed}</span>
                      </div>
                    </div>
                    
                    {/* Executed date in bottom right */}
                    <div className="absolute bottom-3 right-4">
                      <span className="text-xs text-muted-foreground/60">
                        {format(new Date(campaign.executed_at), 'MMM d, HH:mm')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Campaign Email Preview Modal */}
      <CampaignEmailPreviewModal
        open={showEmailPreview}
        onOpenChange={setShowEmailPreview}
        campaignRule={campaignRules.find(rule => rule.id === selectedRule) || { id: '', name: '', description: '' }}
        targetedUsers={targetedUsers}
        selectedRecipients={selectedRecipients}
        templateType={(campaignRules.find(rule => rule.id === selectedRule)?.template_type || "subscription_pending_completion") as TemplateType}
        onConfirmSend={handleEmailPreviewConfirm}
      />

      {/* Execute Campaign Confirmation Dialog */}
      <Dialog open={showExecuteDialog} onOpenChange={setShowExecuteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Campaign Execution</DialogTitle>
            <DialogDescription>
              Are you sure you want to send emails to {selectedRecipients.length} recipients? 
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-700">
                    Email activity will be tracked in the communication logs for your records.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExecuteDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={confirmExecuteCampaign}
              disabled={executeCampaignMutation.isPending}
              className="flex items-center gap-2"
            >
              {executeCampaignMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {executeCampaignMutation.isPending ? 'Sending...' : 'Send Campaign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CampaignManagement;